import classNames from 'classnames';
import { forwardRef } from 'react';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import DownloadPdfBtn from '@/shared/components/shared/DownloadPdfBtn/DownloadPdfBtn';
import RoundButton from '@/shared/components/shared/RoundButton';
import Typografed from '@/shared/components/shared/Typografed';
import { tp } from '@/shared/lib/typograf';
import Link from '../../shared/Link';
import TariffCard from './TariffCard';
import TariffsOptionsPopup, { TARIFFS_OPTIONS_POPUP_NAME } from './TariffsOptionsPopup';
import type { TariffsInfoSectionWithPopupProps } from './types';

export type { TariffWithPopupItem, TariffsInfoSectionWithPopupProps } from './types';

type Props = Omit<React.HTMLAttributes<HTMLElement>, 'title'> & TariffsInfoSectionWithPopupProps;

export const SECTION_ID = 'tarifs';

const TariffsInfoSectionWithPopup = forwardRef<HTMLDivElement, Props>(
    (
        {
            title,
            subtitle,
            download,
            popupTitle,
            allTariffFeatures,
            tariffs,
            discounts,
            discountPriceLabel,
            futurePriceLabel,
            notes,
            footnotes,
            contacts,
            ...props
        },
        ref,
    ) => {
        const { openPopup } = useSetOpenedPopupsState();
        const filteredTariffs = tariffs?.filter((tariff) => tariff.title) ?? [];
        const features = allTariffFeatures ?? [];
        const hasHeader = Boolean(title || subtitle || download?.href);
        const hasContacts = Boolean(contacts && (contacts.title || contacts.text || contacts.items.length > 0));
        const hasNotes = Boolean(notes && notes.length > 0);
        const hasFootnotes = Boolean(footnotes);

        if (!hasHeader && filteredTariffs.length === 0 && !hasContacts && !hasNotes && !hasFootnotes) {
            return null;
        }

        const openOptionsPopup = () => {
            openPopup(TARIFFS_OPTIONS_POPUP_NAME);
        };

        return (
            <section
                {...props}
                ref={ref}
                className={classNames(
                    'tariffs-info-section-with-popup wrapper js-header-theme-trigger',
                    props.className,
                )}
                data-theme="dark"
                id={props.id ?? SECTION_ID}
            >
                {hasHeader && (
                    <div className="tariffs-info-section-with-popup__top">
                        {title && (
                            <div className="tariffs-info-section-with-popup__title-wrapper">
                                <div className="h3 tariffs-info-section-with-popup__title">
                                    <Typografed>{title}</Typografed>
                                </div>
                            </div>
                        )}
                        {subtitle && (
                            <p className="text-s tariffs-info-section-with-popup__subtitle">
                                <Typografed>{subtitle}</Typografed>
                            </p>
                        )}
                        {download?.href && (
                            <DownloadPdfBtn
                                title={download.text || 'Скачать тарифы'}
                                href={download.href}
                                className="tariffs-info-section-with-popup__download"
                            />
                        )}
                    </div>
                )}

                {(filteredTariffs.length > 0 || hasContacts) && (
                    <div className="tariffs-info-section-with-popup__content">
                        {filteredTariffs.length > 0 && (
                            <ul
                                className={classNames(
                                    'tariffs-info-section-with-popup__list list-unstyled',
                                    `tariffs-info-section-with-popup__list--${filteredTariffs.length}`,
                                )}
                            >
                                {filteredTariffs.map((tariff) => (
                                    <TariffCard
                                        key={tariff.id}
                                        title={tariff.title}
                                        text={tariff.text}
                                        color={tariff.color}
                                        price={tariff.price}
                                        priceNote={tariff.priceNote}
                                        button={tariff.button}
                                        allOptionsLabel="Все опции"
                                        onAllOptionsClick={openOptionsPopup}
                                    />
                                ))}
                            </ul>
                        )}

                        {hasContacts && contacts && (
                            <div className="tariffs-info-section-with-popup__contacts">
                                {(contacts.title || contacts.text) && (
                                    <div className="tariffs-info-section-with-popup__contacts-header">
                                        {contacts.title && (
                                            <div className="text-l tariffs-info-section-with-popup__contacts-title">
                                                <Typografed>{contacts.title}</Typografed>
                                            </div>
                                        )}
                                        {contacts.text && (
                                            <div
                                                className="text-xs tariffs-info-section-with-popup__contacts-text"
                                                dangerouslySetInnerHTML={{ __html: tp(contacts.text) }}
                                            />
                                        )}
                                    </div>
                                )}
                                {contacts.items.length > 0 && (
                                    <ul className="list-unstyled tariffs-info-section-with-popup__contact-list">
                                        {contacts.items.map((item) => (
                                            <li key={item.href}>
                                                <Link
                                                    href={item.href}
                                                    className="tariffs-info-section-with-popup__contact-item"
                                                >
                                                    {item.icon && (
                                                        <RoundButton
                                                            tag="span"
                                                            className="tariffs-info-section-with-popup__contact-item__icon"
                                                        >
                                                            <span
                                                                className="tariffs-info-section-with-popup__contact-item__icon-img"
                                                                style={
                                                                    {
                                                                        '--icon-url': `url(${item.icon.src})`,
                                                                    } as React.CSSProperties
                                                                }
                                                                aria-hidden="true"
                                                            />
                                                        </RoundButton>
                                                    )}
                                                    <span className="text-xs tariffs-info-section-with-popup__contact-item__label">
                                                        {item.label}
                                                    </span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {(hasNotes || hasFootnotes) && (
                    <div className="tariffs-info-section-with-popup__bottom">
                        {hasFootnotes && footnotes && (
                            <div
                                className="tariffs-info-section-with-popup__note text-s text-opacity wysiwyg"
                                dangerouslySetInnerHTML={{ __html: tp(footnotes) }}
                            />
                        )}
                        {hasNotes &&
                            notes?.map((note) => (
                                <p key={note} className="tariffs-info-section-with-popup__note text-s text-opacity">
                                    <Typografed>{note}</Typografed>
                                </p>
                            ))}
                    </div>
                )}

                {filteredTariffs.length > 0 && features.length > 0 && (
                    <TariffsOptionsPopup
                        title={popupTitle}
                        allTariffFeatures={features}
                        tariffs={filteredTariffs}
                        discounts={discounts}
                        discountPriceLabel={discountPriceLabel}
                        futurePriceLabel={futurePriceLabel}
                    />
                )}
            </section>
        );
    },
);

TariffsInfoSectionWithPopup.displayName = 'TariffsInfoSectionWithPopup';

export default TariffsInfoSectionWithPopup;
